import type { AgentsSettings, CardColor, EndReason, PackId, TeamId } from '../shared/index.js';

/** Türkçe tamlayan eki: Deniz → ’in, Ali → ’nin, Ufuk → ’un. */
function possessive(name: string): string {
  const lower = name.toLocaleLowerCase('tr');
  const vowels = [...lower].filter((ch) => 'aeıioöuü'.includes(ch));
  const last = vowels.at(-1) ?? 'e';
  const v = 'aı'.includes(last) ? 'ı' : 'ei'.includes(last) ? 'i' : 'ou'.includes(last) ? 'u' : 'ü';
  const endsVowel = 'aeıioöuü'.includes(lower.at(-1) ?? '');
  return `’${endsVowel ? 'n' : ''}${v}n`;
}

export const s = {
  name: 'Ajanlar',
  pitch: 'Tek kelimelik ipucuyla takımına gizli ajanlarını buldur',
  team: { a: 'Yeşil takım', b: 'Mor takım' } satisfies Record<TeamId, string>,
  teamShort: { a: 'Yeşil', b: 'Mor' } satisfies Record<TeamId, string>,
  color: { a: 'Yeşil ajan', b: 'Mor ajan', neutral: 'Tarafsız', assassin: 'Suikastçı' } satisfies Record<CardColor, string>,
  settings: {
    teams: 'Takımlar',
    teamsHint: 'Bir oyuncuya dokunarak takımını değiştir. Takımı seçilmeyenler başlarken dağıtılır.',
    unassigned: 'Takımsız',
    shuffle: 'Karıştır',
    shuffleAria: 'Takımları karıştır',
    offline: 'bağlantı yok',
    empty: 'Henüz kimse yok',
    needTwo: 'Her takımda en az 2 kişi olmalı.',
    leaders: 'Liderler',
    leaderPick: { random: 'Başlarken rastgele', host: 'Oda sahibi seçer' } satisfies Record<AgentsSettings['leaderPick'], string>,
    leaderHint: 'Lider ipucunu verir ve gizli anahtarı görür. Her yeni tabloda lider sırayla değişir.',
    pickLeader: (team: string) => `${team} lideri`,
    noLeader: 'Seçilmedi, rastgele olacak',
    packs: 'Kelime paketleri',
    pack: { genel: 'Genel', kolay: 'Kolay', zor: 'Zor' } satisfies Record<PackId, string>,
    packHint: 'Seçili paketlerden 25 kelime çekilir.',
    reveal: 'Kart nasıl açılsın',
    revealMode: { first: 'İlk dokunuş açar', vote: 'Takım oylaması' } satisfies Record<AgentsSettings['revealMode'], string>,
    revealHint: {
      first: 'Takımdan biri karta dokununca kart açılır.',
      vote: 'Takımın çoğunluğu aynı karta dokununca açılır. Herkes kimin neye dokunduğunu görür.',
    } satisfies Record<AgentsSettings['revealMode'], string>,
    timer: 'Süre',
    timerMode: { off: 'Süresiz', split: 'Lider ve takım ayrı', total: 'Sıra başına toplam' } satisfies Record<AgentsSettings['timer'], string>,
    timerHint: {
      off: 'Acele yok; sıra ipucu ve tahminle ilerler.',
      split: 'Lider ipucu için, takım tahmin için ayrı ayrı bu süreyi alır.',
      total: 'İpucu ve tahminler aynı sürenin içine sığmalı.',
    } satisfies Record<AgentsSettings['timer'], string>,
    sec: (n: number) => `${n} sn`,
    readOnly: 'Ayarları oda sahibi seçer.',
    liveHint: 'Oyun sırasındaki değişiklikler sıradaki sıradan, paket ve takımlar yeni tablodan itibaren geçerli olur.',
  },
  play: {
    board: (n: number) => `Tablo ${n}`,
    pill: (n: number) => `Ajanlar · Tablo ${n}`,
    agents: (n: number) => `${n} ajan`,
    clueOf: (nick: string) => `Lider ${nick}${possessive(nick)} ipucu`,
    myClue: 'Senin ipucun',
    thinking: '…',
    yourClue: 'İpucun',
    legendNeutral: 'Tarafsız: sıra biter',
    legendAssassin: 'Suikastçı: oyun biter',
    turnShort: (team: string) => `Sıra ${team.toLocaleLowerCase('tr')}da`,
    you: 'sen',
    leader: 'lider',
    nextLeader: 'sıradaki lider',
    makeLeader: 'Lider yap',
    makeNextLeader: 'Sıradaki tabloda lider yap',
    offline: 'bağlantı yok',
    left: (n: number) => (n === 1 ? '1 ajan kaldı' : `${n} ajan kaldı`),
    turnOf: (team: string) => `Sıra ${team.toLocaleLowerCase('tr')}da`,
    waitClue: (nick: string) => `${nick} ipucu düşünüyor.`,
    waitClueMine: 'Liderin ipucu düşünüyor. Tabloya bakıp hazırlan.',
    leaderOffline: (nick: string) => `${nick} bağlı değil. Oda sahibi takımdan başka birini lider yapabilir.`,
    clueLabel: 'İpucu',
    cluePlaceholder: 'Tek kelime',
    count: 'Kaç kart',
    unlimited: 'Sınırsız',
    giveClue: 'İpucunu ver',
    clueHint: 'Tablodaki açılmamış bir kelimeyi ya da onun kökünü kullanamazsın.',
    guessesLeft: (n: number | null) => (n === null ? 'Sınırsız hak' : n === 1 ? '1 hak kaldı' : `${n} hak kaldı`),
    tapHint: { first: 'Takımınla konuş, sonra karta dokun.', vote: 'Karta dokunarak oy ver. Tekrar dokunursan oyunu geri çekersin.' },
    votes: (n: number, need: number) => `${n}/${need} oy`,
    votesNeed: (need: number) => (need === 1 ? 'Kart tek oyla açılır.' : `Bir kartın açılması için ${need} oy gerekir.`),
    endTurn: 'Sırayı bitir',
    leaderGuessing: 'Takımın tahmin ediyor. Yüzün bir şey belli etmesin.',
    opponentGuessing: (team: string) => `${team} tahmin ediyor.`,
    spectator: 'Bu tabloyu izliyorsun.',
    key: 'Anahtar',
    keyHint: 'Yalnızca liderler görür.',
    history: 'İpuçları',
    historyEmpty: 'Henüz ipucu verilmedi.',
    noClue: 'süre doldu',
    noPicks: 'kart açılmadı',
    wins: 'Kazanılan tablolar',
    seconds: 'sn',
  },
  over: {
    kicker: 'Tablo bitti',
    kickerFinal: 'Oyun bitti',
    won: (team: string) => `${team} kazandı!`,
    found: (team: string, n: number, total: number) => `${team}: ${total} ajandan ${n} tanesini buldu`,
    ended: 'Tablo yarıda kaldı',
    reason: {
      agents: 'Bütün ajanlarını buldular.',
      assassin: 'Rakip suikastçıyı açtı.',
      ended: 'Oda sahibi oyunu bitirdi.',
    } satisfies Record<EndReason, string>,
    keyShown: 'Anahtar artık herkese açık.',
    newBoard: 'Yeni tablo',
    newBoardHint: 'Aynı takımlarla devam; liderler sırayla değişir.',
    toLobby: 'Lobiye dön',
    waitHost: 'Oda sahibi yeni tablo açabilir ya da lobiye dönebilir.',
    closing: 'Lobiye dönülüyor…',
  },
  howto: {
    title: 'Nasıl oynanır',
    steps: [
      ['Takımlar ve liderler', 'Yeşil ve mor iki takım var. Her takımın bir lideri olur; lider gizli anahtarı görür, diğerleri yalnızca kelimeleri.'],
      ['İpucu', 'Sırası gelen lider tek kelimelik bir ipucu ve bir sayı verir: “deniz 3” üç kartın denizle ilgili olduğunu söyler.'],
      ['Tahmin', 'Takım kartlara dokunur. Kendi ajanınızsa devam edersiniz, en fazla sayının bir fazlası kadar. İstediğiniz an sırayı bitirebilirsiniz.'],
      ['Tuzaklar', 'Tarafsız kart sırayı bitirir. Rakibin ajanı rakibe sayılır. Suikastçıyı açan takım anında kaybeder.'],
      ['Kazanmak', 'Bütün ajanlarını önce bulan takım kazanır. Başlayan takımın 9, diğerinin 8 ajanı var.'],
    ] as [string, string][],
  },
};
